import { apiRequest, ApiError } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';
import { normalizeBotServiceHours } from '@/lib/bot-service-hours';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function normalizeGateways(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const item of list) {
    const botId = String(item?.botId || '').trim();
    if (!botId) continue;
    out.push({ botId, label: String(item?.label || '').trim() || botId });
  }
  return out;
}

function normalizeBotServiceHoursPayload(payload) {
  return {
    settings: normalizeBotServiceHours(payload?.settings),
    gateways: normalizeGateways(payload?.gateways),
    placeholders: Array.isArray(payload?.placeholders) ? payload.placeholders.map(String) : [],
    canEdit: Boolean(payload?.canEdit),
    // Absent on a never-saved document; the PUT's optimistic-concurrency base. Sent back
    // verbatim as baseUpdatedAt so a stale editor gets a 409 instead of clobbering a save.
    updatedAt: payload?.updatedAt ? String(payload.updatedAt) : null,
    updatedBy: String(payload?.updatedBy || ''),
    raw: payload,
  };
}

/**
 * Org-wide bot working-hours configuration (Settings → Bot working hours) from the shared
 * global_settings collection. Readable by every active employee; the payload carries the
 * WhatsApp gateway (number) catalog so the editor needs no second fetch.
 * GET /api/settings/bot-service-hours → { settings, gateways, placeholders, canEdit,
 * updatedAt?, updatedBy? }. 404 on a backend that predates the feature (the sub-page
 * renders a read-only "not supported" note).
 */
export async function fetchBotServiceHours({ token } = {}) {
  const payload = await apiRequest('/api/settings/bot-service-hours', {
    method: 'GET',
    headers: createAuthHeaders(token),
    cache: 'no-store',
  });
  return normalizeBotServiceHoursPayload(payload);
}

/**
 * Full replace of the bot working-hours settings. PUT /api/settings/bot-service-hours
 * body { settings, baseUpdatedAt? }. `settings` must be the COMPLETE value — version,
 * bypassPhones and emergencies included even when the editor did not touch them — because
 * the server replaces every known field. Requires settings:manage (403 otherwise); 400 on
 * validation failure; 409 with { error, updatedAt } when someone else saved since
 * baseUpdatedAt was loaded (refetch, rebase, retry). Answers the GET shape.
 * ApiErrors propagate so callers can branch on .status (404 / 409).
 */
export async function putBotServiceHours({ token, settings, baseUpdatedAt } = {}) {
  const body = { settings: normalizeBotServiceHours(settings) };
  if (baseUpdatedAt) {
    body.baseUpdatedAt = String(baseUpdatedAt);
  }
  const payload = await apiRequest('/api/settings/bot-service-hours', {
    method: 'PUT',
    headers: createAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  return normalizeBotServiceHoursPayload(payload);
}
